import { useState, useEffect } from 'react'
import { clsx } from 'clsx'
import {
  Plus,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  Building2,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  XCircle,
  AlertCircle,
  Copy,
  KeyRound,
  Check,
} from 'lucide-react'
import { tenantApi } from '@/api'
import type { Tenant } from '@/types'
import TableLoading from '@/components/ui/TableLoading'

type FormData = {
  name: string
  short_name: string
  code: string
  contact_email: string
  contact_phone: string
  address: string
  vehicle_categories: Array<{ name: string }>
  payment_methods: Array<{ name: string }>
  external_api_key: string
}

export default function TenantsPage() {
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null)
  const [apiKeyCopied, setApiKeyCopied] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    short_name: '',
    code: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    vehicle_categories: [],
    payment_methods: [],
    external_api_key: '',
  })

  // 加载租户列表
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTenants()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const loadTenants = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await tenantApi.list({ search: search || undefined })
      setTenants(response.items)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '加載租戶失败'
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRefresh = async () => {
    await loadTenants()
  }

  const copyApiKey = () => {
    if (formData.external_api_key) {
      navigator.clipboard.writeText(formData.external_api_key)
      setApiKeyCopied(true)
      setTimeout(() => setApiKeyCopied(false), 2000)
    }
  }

  const handleOpenCreate = () => {
    setEditingTenant(null)
    setFormData({
      name: '',
      short_name: '',
      code: '',
      contact_email: '',
      contact_phone: '',
      address: '',
      vehicle_categories: [],
      payment_methods: [],
      external_api_key: '',
    })
    setShowModal(true)
  }

  const handleOpenEdit = (tenant: Tenant) => {
    setEditingTenant(tenant)
    setFormData({
      name: tenant.name,
      short_name: tenant.short_name || '',
      code: tenant.code,
      contact_email: tenant.contact_email,
      contact_phone: tenant.contact_phone,
      address: tenant.address || '',
      vehicle_categories: tenant.vehicle_categories || [],
      payment_methods: tenant.payment_methods || [],
      external_api_key: (tenant.form_config as any)?.external_api_key || '',
    })
    setShowModal(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setError(null)

    try {
      const existingFormConfig = editingTenant ? ((editingTenant.form_config as any) || {}) : {}
      const newFormConfig = { ...existingFormConfig, external_api_key: formData.external_api_key || undefined }

      if (editingTenant) {
        await tenantApi.update(editingTenant.id, {
          name: formData.name,
          short_name: formData.short_name || undefined,
          code: formData.code,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          address: formData.address || undefined,
          vehicle_categories: formData.vehicle_categories,
          payment_methods: formData.payment_methods,
          form_config: newFormConfig,
        })
      } else {
        await tenantApi.create({
          name: formData.name,
          short_name: formData.short_name || undefined,
          code: formData.code,
          contact_email: formData.contact_email,
          contact_phone: formData.contact_phone,
          address: formData.address || undefined,
          vehicle_categories: formData.vehicle_categories,
          payment_methods: formData.payment_methods,
          form_config: formData.external_api_key ? newFormConfig : undefined,
        })
      }

      setShowModal(false)
      await loadTenants()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '操作失败'
      setError(errorMsg)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('確定要刪除這個租戶嗎？')) {
      return
    }

    try {
      setError(null)
      await tenantApi.delete(id)
      await loadTenants()
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '刪除失败'
      setError(errorMsg)
    }
  }

  const toggleActive = async (id: string) => {
    try {
      setError(null)
      const tenant = tenants.find((t) => t.id === id)
      if (tenant) {
        await tenantApi.update(id, { is_active: !tenant.is_active })
        await loadTenants()
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '更新狀態失败'
      setError(errorMsg)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            租戶管理
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            管理多租戶資訊與設定
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors duration-150"
        >
          <Plus className="h-4 w-4" />
          新增租戶
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-3 rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-400">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-400 hover:text-red-600 dark:hover:text-red-300"
          >
            ✕
          </button>
        </div>
      )}

      {/* Search & Filter */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            placeholder="搜尋租戶名稱、代碼或信箱..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-gray-50 py-2 pl-10 pr-4 text-sm text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
          />
        </div>
        <button
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors duration-150"
        >
          <RefreshCw className={clsx('h-4 w-4', isLoading && 'animate-spin')} />
          重新整理
        </button>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  租戶資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  短名稱
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  聯絡資訊
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  車輛類別
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  付款方式
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  狀態
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <TableLoading colSpan={5} message="正在加載租戶..." />
              ) : tenants.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Building2 className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      找不到符合條件的租戶
                    </p>
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr
                    key={tenant.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900">
                          <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {tenant.name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {tenant.code}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {tenant.short_name || <span className="text-gray-400">未設定</span>}
                      </p>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1 text-sm">
                        <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <Mail className="h-4 w-4 text-gray-400" />
                          {tenant.contact_email}
                        </div>
                        <div className="flex items-center gap-2 text-gray-900 dark:text-white">
                          <Phone className="h-4 w-4 text-gray-400" />
                          {tenant.contact_phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {tenant.vehicle_categories && tenant.vehicle_categories.length > 0 ? (
                        <div className="space-y-1">
                          {tenant.vehicle_categories.map((cat, idx) => (
                            <div key={idx} className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-700 dark:bg-blue-900/50 dark:text-blue-400 mr-1">
                              {cat.name}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">未設定</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {tenant.payment_methods && tenant.payment_methods.length > 0 ? (
                        <div className="space-y-1">
                          {tenant.payment_methods.map((method, idx) => (
                            <div key={idx} className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs text-green-700 dark:bg-green-900/50 dark:text-green-400 mr-1">
                              {method.name}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">未設定</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleActive(tenant.id)}
                        className={clsx(
                          'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors duration-150',
                          tenant.is_active
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400'
                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-400'
                        )}
                      >
                        {tenant.is_active ? (
                          <CheckCircle className="h-3.5 w-3.5" />
                        ) : (
                          <XCircle className="h-3.5 w-3.5" />
                        )}
                        {tenant.is_active ? '啟用' : '停用'}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenEdit(tenant)}
                          className="rounded-lg p-2 text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700 transition-colors duration-150"
                          title="編輯"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(tenant.id)}
                          className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 transition-colors duration-150"
                          title="刪除"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowModal(false)}
          />

          {/* Modal */}
          <div className="relative z-10 w-full max-w-2xl rounded-xl bg-white shadow-xl dark:bg-gray-800 flex flex-col max-h-[90vh]">
            <div className="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingTenant ? '編輯租戶' : '新增租戶'}
              </h2>
            </div>

            <form id="tenant-form" onSubmit={handleSubmit} className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
              {/* Row 1: 租戶名稱 + 短名稱 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    租戶名稱 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    短名稱
                  </label>
                  <input
                    type="text"
                    value={formData.short_name}
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Row 2: 租戶代碼 + 聯絡電話 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    租戶代碼 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    disabled={!!editingTenant}
                    className={clsx(
                      'mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white',
                      editingTenant && 'cursor-not-allowed bg-gray-50 dark:bg-gray-600'
                    )}
                  />
                  {editingTenant && (
                    <p className="mt-1 text-xs text-gray-500">租戶代碼無法修改</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    聯絡電話 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>

              {/* Row 3: 聯絡信箱 (full width) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  聯絡信箱 <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              {/* Row 4: 地址 (full width) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  地址
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  車輛類別
                </label>
                <div className="mt-2 space-y-2">
                  {formData.vehicle_categories.map((cat, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={cat.name}
                        onChange={(e) => {
                          const updated = [...formData.vehicle_categories]
                          updated[idx].name = e.target.value
                          setFormData({ ...formData, vehicle_categories: updated })
                        }}
                        placeholder="例：轎車、SUV"
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            vehicle_categories: formData.vehicle_categories.filter((_, i) => i !== idx)
                          })
                        }}
                        className="rounded-lg bg-red-100 px-3 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900"
                      >
                        移除
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        vehicle_categories: [...formData.vehicle_categories, { name: '' }]
                      })
                    }}
                    className="w-full rounded-lg border border-blue-300 bg-blue-50 py-2 text-sm text-blue-600 hover:bg-blue-100 dark:border-blue-700 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50"
                  >
                    + 添加車輛類別
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  付款方式
                </label>
                <div className="mt-2 space-y-2">
                  {formData.payment_methods.map((method, idx) => (
                    <div key={idx} className="flex gap-2">
                      <input
                        type="text"
                        value={method.name}
                        onChange={(e) => {
                          const updated = [...formData.payment_methods]
                          updated[idx].name = e.target.value
                          setFormData({ ...formData, payment_methods: updated })
                        }}
                        placeholder="例：現金、信用卡"
                        className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFormData({
                            ...formData,
                            payment_methods: formData.payment_methods.filter((_, i) => i !== idx)
                          })
                        }}
                        className="rounded-lg bg-red-100 px-3 py-2 text-red-600 hover:bg-red-200 dark:bg-red-900/50 dark:text-red-400 dark:hover:bg-red-900"
                      >
                        移除
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        payment_methods: [...formData.payment_methods, { name: '' }]
                      })
                    }}
                    className="w-full rounded-lg border border-green-300 bg-green-50 py-2 text-sm text-green-600 hover:bg-green-100 dark:border-green-700 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                  >
                    + 添加付款方式
                  </button>
                </div>
              </div>

              {/* External API Key */}
              <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 space-y-2">
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <KeyRound className="h-4 w-4 text-blue-500" />
                  外部訂單 API Key
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={formData.external_api_key}
                    placeholder="尚未設定，點右方按鈕產生"
                    className="flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-mono text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 cursor-default"
                  />
                  <button
                    type="button"
                    onClick={copyApiKey}
                    disabled={!formData.external_api_key}
                    title="複製"
                    className="rounded-lg border border-gray-300 px-3 py-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    {apiKeyCopied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, external_api_key: crypto.randomUUID() })}
                    title="產生新 Key"
                    className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 cursor-pointer whitespace-nowrap"
                  >
                    產生 Key
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  用於外部系統呼叫 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">POST /api/v1/public/external/orders</code> 時的 <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">X-Api-Key</code> 標頭
                </p>
              </div>

            </form>

            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                disabled={isSubmitting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors duration-150"
              >
                取消
              </button>
              <button
                type="submit"
                form="tenant-form"
                disabled={isSubmitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-150"
              >
                {isSubmitting ? '處理中...' : editingTenant ? '更新' : '新增'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
